import { useState, useEffect } from 'react';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { useBulkLocks } from '../hooks/useBulkLocks';
import { LockIndicator } from '../components/permissions/LockIndicator';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
    Dialog, DialogContent, DialogTitle,
} from '../components/ui/dialog';
import { ClipboardList, Eye, Trash2, Loader2, Search, User } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { InventaireDetailContent } from '../components/inventaires/InventaireDetailDialog';
import { InventaireFormContent } from '../components/inventaires/InventaireFormDialog';
import { ReadOnlyGuard } from '../components/permissions/ReadOnlyGuard';
import { listsEqualById } from '../lib/listDiff';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
    nouveau: 'bg-blue-100 text-blue-700 border-blue-200',
    en_redaction: 'bg-orange-100 text-orange-700 border-orange-200',
    termine: 'bg-emerald-100 text-emerald-700 border-emerald-200',
};

const TYPE_LABELS = {
    visite_presentielle: 'Visite présentielle',
    visite_virtuelle: 'Visite virtuelle',
    listing_email: 'Listing email',
    listing_telephone: 'Listing téléphone',
    devis_estimatif: 'Devis estimatif',
};

function getTypeLabel(type) {
    return TYPE_LABELS[type] || type || '-';
}

function timeAgo(dateStr) {
    if (!dateStr) return '-';
    try {
        // Backend envoie parfois des ISO sans timezone — les traiter comme UTC
        const hasTZ = /[zZ]|[+-]\d\d:?\d\d$/.test(dateStr);
        const d = new Date(hasTZ ? dateStr : dateStr + 'Z');
        const now = new Date();
        const diffMs = now - d;
        const diffMin = Math.floor(diffMs / (1000 * 60));
        const diffH = Math.floor(diffMs / (1000 * 60 * 60));
        const diffD = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        if (diffMin < 1) return "à l'instant";
        if (diffMin < 60) return `il y a ${diffMin} min`;
        if (diffH < 24) return `il y a ${diffH}h`;
        if (diffD < 30) return `il y a ${diffD}j`;
        return d.toLocaleDateString('fr-FR');
    } catch { return '-'; }
}

export function InventairesPage() {
    const { user } = useAuth();
    const location = useLocation();
    const [inventaires, setInventaires] = useState([]);
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [selectedInventaire, setSelectedInventaire] = useState(null);
    const [dialogMode, setDialogMode] = useState(null);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const fetchConfig = async () => {
        try {
            const { data } = await axios.get(`${API}/api/inventaires/config`, { withCredentials: true });
            setConfig(data);
        } catch {}
    };

    const fetchInventaires = async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const params = {};
            if (search) params.search = search;
            if (statusFilter !== 'all') params.statut = statusFilter;
            const { data } = await axios.get(`${API}/api/inventaires`, { params, withCredentials: true });
            const sorted = [...data].sort((a, b) => {
                const aNew = a.statut === 'nouveau' ? 1 : 0;
                const bNew = b.statut === 'nouveau' ? 1 : 0;
                return bNew - aNew;
            });
            setInventaires(prev => listsEqualById(prev, sorted) ? prev : sorted);
        } catch { if (!silent) toast.error('Erreur lors du chargement'); }
        finally { if (!silent) setLoading(false); }
    };

    useEffect(() => { fetchConfig(); }, []);
    useEffect(() => { fetchInventaires(); }, [search, statusFilter]);

    useAutoRefresh(() => fetchInventaires(true));

    const inventaireLocks = useBulkLocks('inventaire', (inventaires || []).map(i => i.id));

    // Auto-open inventaire detail when navigated with state or query param
    useEffect(() => {
        const openId = location.state?.open_inventaire_id || new URLSearchParams(location.search).get('open');
        if (openId) {
            window.history.replaceState({}, '', '/inventaires');
            axios.get(`${API}/api/inventaires/${openId}`, { withCredentials: true })
                .then(({ data }) => { setSelectedInventaire(data); setDialogMode('detail'); })
                .catch(() => { toast.error('Inventaire introuvable'); });
        }
    }, [location.state, location.search]);

    const getStatusLabel = (s) => config?.statuses?.find(x => x.value === s)?.label || s;
    const getTypeLabel = (t) => config?.types?.find(x => x.value === t)?.label || t;

    const handleView = (inv) => { setSelectedInventaire(inv); setDialogMode('detail'); };
    const handleEdit = () => setDialogMode('edit');
    const handleCloseDialog = () => { setDialogMode(null); setSelectedInventaire(null); };

    const handleEditSuccess = async () => {
        await fetchInventaires();
        if (selectedInventaire) {
            try {
                const { data } = await axios.get(`${API}/api/inventaires/${selectedInventaire.id}`, { withCredentials: true });
                setSelectedInventaire(data);
            } catch {}
        }
        setDialogMode('detail');
    };

    const handleDetailRefresh = async () => {
        await fetchInventaires();
        if (selectedInventaire) {
            try {
                const { data } = await axios.get(`${API}/api/inventaires/${selectedInventaire.id}`, { withCredentials: true });
                setSelectedInventaire(data);
            } catch {}
        }
    };

    const handleDelete = async () => {
        if (!selectedInventaire) return;
        setIsDeleting(true);
        try {
            await axios.delete(`${API}/api/inventaires/${selectedInventaire.id}`, { withCredentials: true });
            toast.success('Inventaire supprimé');
            setIsDeleteOpen(false);
            setSelectedInventaire(null);
            fetchInventaires();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        } finally { setIsDeleting(false); }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        try {
            return new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
        } catch { return '-'; }
    };

    return (
        <div className="p-4 md:p-6 lg:p-8" data-testid="inventaires-page">
            <ReadOnlyGuard module="inventaires">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                <div>
                    <h1
                        className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight"
                        style={{ fontFamily: 'Manrope, sans-serif' }}
                        data-testid="inventaires-title"
                    >
                        Inventaires
                    </h1>
                    <p className="mt-1 text-slate-500">
                        Gérez vos inventaires clients
                    </p>
                </div>
            </div>

            {/* Filters */}
            <Card className="bg-white rounded-xl border border-slate-200 shadow-sm mb-6">
                <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="relative flex-1">
                            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input
                                placeholder="Rechercher (nom, téléphone, email)..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-10"
                                data-testid="inventaires-search"
                            />
                        </div>
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger className="w-full sm:w-[200px]" data-testid="inventaires-filter-status">
                                <SelectValue placeholder="Filtrer par statut" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tous les statuts</SelectItem>
                                {config?.statuses?.map((s) => (
                                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            {/* Inventaires Table */}
            <Card className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg font-semibold text-slate-800">
                        <ClipboardList size={20} className="text-[#2e7cd6]" />
                        Liste des inventaires ({inventaires.length})
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0 sm:p-6">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-[#2e7cd6]" />
                        </div>
                    ) : inventaires.length === 0 ? (
                        <div className="text-center py-12 text-slate-500">
                            Aucun inventaire trouvé
                        </div>
                    ) : (
                        <div className="overflow-x-auto -mx-0">
                            <Table data-testid="inventaires-table" className="min-w-[700px]">
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Client</TableHead>
                                        <TableHead>Date de création</TableHead>
                                        <TableHead>Type</TableHead>
                                        <TableHead>Commercial</TableHead>
                                        <TableHead>Statut</TableHead>
                                        <TableHead>Modifié</TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {inventaires.map((inv) => (
                                        <TableRow key={inv.id} data-testid={`inventaire-row-${inv.id}`} className="cursor-pointer hover:bg-slate-50" onClick={() => handleView(inv)}>
                                            <TableCell>
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                                                        <User size={20} className="text-slate-500" />
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-slate-900 flex items-center gap-2">
                                                            {inv.client_nom || '-'}
                                                            <LockIndicator lock={inventaireLocks[inv.id]} />
                                                        </p>
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-slate-600">
                                                {formatDate(inv.created_at)}
                                            </TableCell>
                                            <TableCell>
                                                {inv.type_inventaire ? (
                                                    <Badge variant="outline" className={`border ${
                                                        inv.type_inventaire === 'visite_presentielle' || inv.type_inventaire === 'visite_virtuelle'
                                                            ? 'bg-emerald-50 text-emerald-700 border-emerald-300'
                                                            : 'bg-purple-50 text-purple-700 border-purple-300'
                                                    }`}>
                                                        {getTypeLabel(inv.type_inventaire)}
                                                    </Badge>
                                                ) : <span className="text-slate-400">-</span>}
                                            </TableCell>
                                            <TableCell>
                                                {inv.commercial_name ? (
                                                    <Badge variant="outline" className="border-slate-300">
                                                        {inv.commercial_couleur && <span className="w-2.5 h-2.5 rounded-full mr-1.5 shrink-0 inline-block" style={{ backgroundColor: inv.commercial_couleur }} />}
                                                        {inv.commercial_name}
                                                    </Badge>
                                                ) : <span className="text-slate-400">-</span>}
                                            </TableCell>
                                            <TableCell>
                                                <Badge
                                                    variant="outline"
                                                    className={`${STATUS_COLORS[inv.statut] || 'bg-slate-100 text-slate-700'} border`}
                                                >
                                                    {getStatusLabel(inv.statut)}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-slate-500 text-sm">
                                                {timeAgo(inv.updated_at || inv.status_changed_at || inv.created_at)}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={(e) => { e.stopPropagation(); handleView(inv); }}
                                                        data-testid={`inventaire-view-${inv.id}`}
                                                    >
                                                        <Eye size={16} className="text-slate-600" />
                                                    </Button>
                                                    {user?.role === 'admin' && (
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={async (e) => {
                                                                e.stopPropagation();
                                                                if (!window.confirm(`Supprimer définitivement l'inventaire de ${inv.client_nom || 'ce client'} ? Action irréversible.`)) return;
                                                                try {
                                                                    await axios.delete(`${API}/api/inventaires/${inv.id}`, { withCredentials: true });
                                                                    toast.success('Inventaire supprimé');
                                                                    fetchInventaires();
                                                                } catch (err) {
                                                                    toast.error('Erreur: ' + (err?.response?.data?.detail || err?.message));
                                                                }
                                                            }}
                                                            data-testid={`inventaire-delete-${inv.id}`}
                                                            title="Supprimer (admin)"
                                                        >
                                                            <Trash2 size={16} className="text-red-500" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Detail/Edit Dialog */}
            <Dialog open={dialogMode !== null} onOpenChange={(open) => { if (!open) handleCloseDialog(); }}>
                <DialogContent
                    className="max-w-5xl w-full max-h-[90dvh] flex flex-col p-0 overflow-hidden"
                    onPointerDownOutside={e => e.preventDefault()}
                    onEscapeKeyDown={e => e.preventDefault()}
                    hideCloseButton
                    data-testid="inventaire-dialog"
                >
                    <DialogTitle className="sr-only">Inventaire</DialogTitle>
                    {dialogMode === 'detail' && selectedInventaire && (
                        <InventaireDetailContent
                            inventaire={selectedInventaire}
                            config={config}
                            onClose={handleCloseDialog}
                            onEdit={handleEdit}
                            onRefresh={handleDetailRefresh}
                        />
                    )}
                    {dialogMode === 'edit' && selectedInventaire && (
                        <InventaireFormContent
                            inventaire={selectedInventaire}
                            config={config}
                            onSuccess={handleEditSuccess}
                            onCancel={() => setDialogMode('detail')}
                        />
                    )}
                </DialogContent>
            </Dialog>

            {/* Delete dialog */}
            <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer l'inventaire ?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Cette action est irréversible. L'inventaire de "{selectedInventaire?.client_nom}" sera définitivement supprimé.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} disabled={isDeleting} className="bg-red-500 hover:bg-red-600">
                            {isDeleting ? 'Suppression...' : 'Supprimer'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
            </ReadOnlyGuard>
        </div>
    );
}
